import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useEffect } from 'react';
import type { AuthenticationResult } from '../../services/AuthService';
import {
  logOut,
  selectRefreshToken,
  selectRememberMe,
  selectToken,
  setCredentials,
} from '../../store/reducers/AuthSlice';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  API_URL,
  API_URL_WITH_API,
  API_LOADER_URL_WITH_API,
  IDENTITY_API_URL_WITH_API,
} from '../Constants';
import {
  getAuthSessionGeneration,
  getRefreshAuthentication,
  invalidatePendingTokenRefreshes,
  terminateAuthSession,
  type TokenRefresh,
} from './authSession';

export const apiClient = axios.create({
  baseURL: API_URL,
});

export const apiClientWithApi = axios.create({
  baseURL: API_URL_WITH_API,
});

export const apiLoaderClientWithApi = axios.create({
  baseURL: API_LOADER_URL_WITH_API,
});

export const identityApiClientWithApi = axios.create({
  baseURL: IDENTITY_API_URL_WITH_API,
});

type AuthAction = ReturnType<typeof logOut> | ReturnType<typeof setCredentials>;

type UnauthorizedResponseOptions = {
  readonly dispatch: (action: AuthAction) => void;
  readonly redirectToLogin?: () => void;
  readonly refreshToken: string | null;
  readonly rememberMe: boolean;
  readonly requestRetried: boolean;
  readonly refreshTokens: TokenRefresh;
  readonly retryRequest: (accessToken: string) => Promise<unknown>;
};

type ExpireAuthSessionOptions = Pick<
  UnauthorizedResponseOptions,
  'dispatch' | 'redirectToLogin'
>;

type RefreshAccessTokenOptions = Pick<
  UnauthorizedResponseOptions,
  'dispatch' | 'redirectToLogin' | 'refreshToken' | 'rememberMe'
> & {
  readonly refreshTokens?: TokenRefresh;
};

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  authRetry?: boolean;
}

const redirectToLogin = () => {
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

const isUnauthorizedResponse = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  if (!('response' in error)) {
    return false;
  }

  const response = error.response;
  return (
    typeof response === 'object' &&
    response !== null &&
    'status' in response &&
    response.status === 401
  );
};

const httpClients = [
  apiClient,
  apiClientWithApi,
  apiLoaderClientWithApi,
  identityApiClientWithApi,
] as const;

const setAuthToken = (token: string | null) => {
  if (token) {
    httpClients.forEach((client) => {
      client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    });
  } else {
    httpClients.forEach((client) => {
      delete client.defaults.headers.common['Authorization'];
    });
  }
};

export const expireAuthSession = ({
  dispatch,
  redirectToLogin: redirect = redirectToLogin,
}: ExpireAuthSessionOptions) => {
  setAuthToken(null);
  terminateAuthSession({
    dispatch,
    redirectToLogin: redirect,
  });
};

export { invalidatePendingTokenRefreshes } from './authSession';

export const requestTokenRefresh: TokenRefresh = async (refreshToken) => {
  const response = await axios.post<AuthenticationResult>(
    `${IDENTITY_API_URL_WITH_API}authorization/refresh`,
    { refreshToken },
  );

  return response.data;
};

export const refreshAccessToken = async ({
  refreshTokens = requestTokenRefresh,
  ...options
}: RefreshAccessTokenOptions): Promise<string | null> => {
  const requestGeneration = getAuthSessionGeneration();

  if (!options.refreshToken) {
    expireAuthSession(options);
    return null;
  }

  let authentication: AuthenticationResult;
  try {
    authentication = await getRefreshAuthentication(
      options.refreshToken,
      refreshTokens,
      requestGeneration,
    );
  } catch {
    if (requestGeneration === getAuthSessionGeneration()) {
      expireAuthSession(options);
    }
    return null;
  }

  if (requestGeneration !== getAuthSessionGeneration()) {
    return null;
  }

  setAuthToken(authentication.token);
  options.dispatch(
    setCredentials({
      response: authentication,
      rememberMe: options.rememberMe,
    }),
  );

  return authentication.token;
};

export const handleUnauthorizedResponse = async (
  error: unknown,
  options: UnauthorizedResponseOptions,
) => {
  if (!isUnauthorizedResponse(error)) {
    return Promise.reject(error);
  }

  if (options.requestRetried) {
    expireAuthSession(options);
    return Promise.reject(error);
  }

  const requestGeneration = getAuthSessionGeneration();
  const accessToken = await refreshAccessToken(options);
  if (
    accessToken === null ||
    requestGeneration !== getAuthSessionGeneration()
  ) {
    return Promise.reject(error);
  }

  return options.retryRequest(accessToken);
};

export const attachUnauthorizedInterceptor = (
  client: AxiosInstance,
  options: Pick<
    UnauthorizedResponseOptions,
    'dispatch' | 'redirectToLogin' | 'refreshToken' | 'rememberMe'
  >,
) =>
  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      const requestConfig: RetryableRequestConfig | null =
        axios.isAxiosError(error) && error.config ? error.config : null;

      return handleUnauthorizedResponse(error, {
        ...options,
        requestRetried: requestConfig?.authRetry === true,
        refreshTokens: requestTokenRefresh,
        retryRequest: (accessToken) => {
          if (!requestConfig) {
            return Promise.reject(error);
          }

          requestConfig.authRetry = true;
          requestConfig.headers.Authorization = `Bearer ${accessToken}`;
          return client.request(requestConfig);
        },
      });
    },
  );

axios.defaults.headers.post['Access-Control-Allow-Credentials'] = true;

export const useAxiosHeaders = () => {
  const token = useAppSelector(selectToken);
  const refreshToken = useAppSelector(selectRefreshToken);
  const rememberMe = useAppSelector(selectRememberMe);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const interceptorIds = httpClients.map((client) =>
      attachUnauthorizedInterceptor(client, {
        dispatch,
        refreshToken,
        rememberMe,
      }),
    );

    return () => {
      httpClients.forEach((client, index) => {
        client.interceptors.response.eject(interceptorIds[index]);
      });
    };
  }, [dispatch, refreshToken, rememberMe]);

  useEffect(() => {
    setAuthToken(token);
  }, [token]);
};
